import { ButtonColors } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { Phone } from '@chiroup/components/form/Phone';
import { Select } from '@chiroup/components/form/Select';
import { ADMIN_USER_ROLE_KEYS, AdminUserRoleKey } from '@chiroup/core/types/AdminUserRole.type';
import { useForm } from '@chiroup/hooks/useForm';
import React, { use, useState } from 'react';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { CreateAdminUserBody, useCreateAdminUser } from '../../hooks/useAdminUsers';
import Modal from '../common/Modal';

const ROLE_LABELS: Record<AdminUserRoleKey, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  developer: 'Developer',
  release_manager: 'Release Manager',
  tech_support: 'Tech Support',
  customer_support: 'Customer Support',
  customer_success: 'Customer Success',
  marketing: 'Marketing',
  finance: 'Finance',
  researcher: 'Researcher',
};

const validation = {
  email: {
    required: { message: 'Email is required.' },
  },
  firstName: {
    required: { message: 'First name is required.' },
  },
  lastName: {
    required: { message: 'Last name is required.' },
  },
  phoneNumber: {
    required: { message: 'Phone number is required.' },
  },
  roleKey: {
    required: { message: 'Role is required.' },
  },
};

type Props = {
  onClose: () => void;
};

export const AdminUserCreateModal: React.FC<Props> = ({ onClose }) => {
  const { hasPermission } = useAdminPermissions();
  const create = useCreateAdminUser();
  const [saving, setSaving] = useState(false);
  const { createToast } = use(ToastContext);

  const { value, registerSubmit, isDirty, errors, onChange } = useForm<CreateAdminUserBody>(
    { roleKey: 'customer_support' },
    validation
  );

  const canAssignSuperAdmin = hasPermission('admin_users.assign_role');
  const allowedRoles = ADMIN_USER_ROLE_KEYS.filter(
    r => r !== 'super_admin' || canAssignSuperAdmin
  ).map(r => ({ value: r, text: ROLE_LABELS[r] }));

  const onSubmit = async (body: Partial<CreateAdminUserBody>) => {
    setSaving(true);
    await create.mutateAsync(body as CreateAdminUserBody);
    setSaving(false);
  };

  const onSuccess = () => {
    createToast({
      title: 'Admin user created',
      description: null,
      type: ToastTypes.Success,
      duration: 5000,
    });
    onClose();
  };

  const onFail = (err: any) => {
    setSaving(false);
    createToast({
      title: 'Failed to create admin user',
      description: err?.response?.data?.message || err?.message || 'An error occurred.',
      type: ToastTypes.Fail,
      duration: 5000,
    });
  };

  return (
    <Modal
      isOpen
      close={onClose}
      header={{ title: 'Add admin user' }}
      footer={{
        buttons: [
          {
            text: 'Cancel',
            onClick: onClose,
            color: ButtonColors.plain,
            className: 'border border-gray-300 dark:border-dark-gray-500',
          },
          {
            text: 'Create',
            onClick: registerSubmit(onSubmit, { onSuccess, onFail }),
            loading: saving,
            disabled: !isDirty,
          },
        ],
      }}
      darkBg
    >
      <div className="space-y-4">
        <Input
          name="email"
          label="Email *"
          type="email"
          value={value.email}
          onChange={onChange('email')}
          errors={errors.fieldErrors?.email}
          autoComplete="off"
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            name="firstName"
            label="First name *"
            value={value.firstName}
            onChange={onChange('firstName')}
            errors={errors.fieldErrors?.firstName}
          />
          <Input
            name="lastName"
            label="Last name *"
            value={value.lastName}
            onChange={onChange('lastName')}
            errors={errors.fieldErrors?.lastName}
          />
        </div>
        <Phone
          name="phoneNumber"
          label="Phone number *"
          value={value.phoneNumber}
          onChange={onChange('phoneNumber')}
          errors={errors.fieldErrors?.phoneNumber}
        />
        <Select
          name="roleKey"
          label="Role *"
          value={value.roleKey}
          onChange={onChange('roleKey')}
          errors={errors.fieldErrors?.roleKey}
          options={allowedRoles}
        />
      </div>
    </Modal>
  );
};
